import * as braze from '@braze/web-sdk';
import { AnalyticsUserData } from '../types';
import BaseTracker, { PlatformProperty } from './BaseTracker';

/**
 * Class for connectivity to Braze for user communications (in-app messaging, push notifications).
 */
export class BrazeTracker extends BaseTracker {
  #brazeKey: string;
  constructor(env: string, platform: PlatformProperty, brazeKey: string) {
    super('braze', 'Braze', env, platform);
    this.#brazeKey = brazeKey;
  }

  initializeBraze() {
    if (!this.isInitialized) {
      braze?.initialize(this.#brazeKey, {
        baseUrl: 'sdk.iad-05.braze.com',
        enableLogging: true,
        allowUserSuppliedJavascript: true,
        minimumIntervalBetweenTriggerActionsInSeconds: 0,
      });
      braze?.automaticallyShowInAppMessages();
      braze?.subscribeToInAppMessage(inAppMessage => {
        braze?.showInAppMessage(inAppMessage);
      });
      this.isInitialized = true;
    }
  }

  /**
   * Performs the actual tracking call.
   * NOT IMPLEMENTED FOR BRAZE YET
   *
   * @param {String} eventName The name of the event to track
   * @returns {Promise} Resolves when the event has been logged.
   */
  private brazeId;
  private acceptedEventsList = [
    'braze_test_event',
    'Show Education Modal',
  ];

  public isInitialized = false;

  openSession = braze?.openSession;
  requestPushPermission = braze?.requestPushPermission;
  isPushPermissionGranted = braze?.isPushPermissionGranted;
  isPushSupported = braze?.isPushSupported;
  isPushBlocked = braze?.isPushBlocked;

  /**
   * Logs the user's current device with Braze.
   * @global window.AppboyPlugin The Braze SDK. Only loaded in the mobile app.
   * @param userData Data from the analytics package that identifies the current user.
   */
  updateUser(userData: AnalyticsUserData) {
    const {
      client_handle, is_logged_in, user_token,
    } = userData;
    const brazeUserId = is_logged_in ? user_token : client_handle;

    if (brazeUserId) {
      if (brazeUserId !== this.brazeId) {
        this.initializeBraze();
        if (window.BrazePlugin) {
          window.BrazePlugin?.changeUser(brazeUserId);
        } else {
          braze?.changeUser(brazeUserId);
        }
        this.brazeId = brazeUserId;
      }
      this.openSession();
    }
  }

  /**
   * Registers custom event with Braze
   * @param eventName name of event to be sent to braze
   * @param properties data to be sent in the Braze custom event
   */
  trackEvent(eventName: string, properties?: object) {
    const eventProperties = {
      external_id: this.brazeId,
      ...properties,
    };

    if (this.acceptedEventsList.includes(eventName)) {
      if (window.BrazePlugin) {
        window.BrazePlugin.logCustomEvent(eventName, eventProperties);
      } else {
        braze?.logCustomEvent(eventName, eventProperties);
      }
    }
  }
}
